import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import MultiSelectTag from "../../../components/MultiSelectTag";
import {
  DSP_OPTIONS, MEDIA_TYPES, DEVICES, ENVIRONMENTS, COUNTRIES,
  STATUS_OPTIONS, KPI_OPTIONS, userLabel,
} from "./dealOptions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = {
  name: "", email: "", company: "", dsp: "", dsp_seat_number: "",
  media_type: [], device: [], environment: [], geography: [],
  custom_request: "", primary_kpi: "", primary_kpi_value: "", primary_kpi_currency: "USD",
  status: "pending", user_id: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[#ebebeb]/40 text-xs font-mono block mb-1">{label}</label>
    {children}
  </div>
);

const AddDealModal = ({ open, onOpenChange, token, users, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.company || !form.dsp || !form.dsp_seat_number) {
      toast.error("Name, email, company, DSP and seat # are required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/deals/admin`, { ...form, user_id: form.user_id || null }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deal created");
      onCreated(res.data);
      setForm(EMPTY);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create deal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] bg-[#191b1d] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="add-deal-modal">
        <DialogHeader>
          <DialogTitle className="font-mono text-xl">Add Deal</DialogTitle>
          <DialogDescription className="text-[#ebebeb]/50">
            Create a deal manually on behalf of a user. No notification emails are sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Field label="Assign to user">
            <select value={form.user_id} onChange={(e) => set("user_id", e.target.value)} className="form-input w-full text-sm" data-testid="add-deal-owner">
              <option value="">Unassigned</option>
              {users.map((u) => (<option key={u.id} value={u.id}>{userLabel(u)}</option>))}
            </select>
            <p className="text-[#ebebeb]/30 text-[11px] font-mono mt-1">
              The assigned user will only see this deal's data in marketplace reporting.
            </p>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact name *">
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="form-input text-sm" data-testid="add-deal-name" />
            </Field>
            <Field label="Email *">
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="form-input text-sm" data-testid="add-deal-email" />
            </Field>
            <Field label="Company *">
              <Input value={form.company} onChange={(e) => set("company", e.target.value)} className="form-input text-sm" data-testid="add-deal-company" />
            </Field>
            <Field label="DSP *">
              <select value={form.dsp} onChange={(e) => set("dsp", e.target.value)} className="form-input w-full text-sm" data-testid="add-deal-dsp">
                <option value="">Select DSP</option>
                {DSP_OPTIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
              </select>
            </Field>
            <Field label="DSP Seat # *">
              <Input value={form.dsp_seat_number} onChange={(e) => set("dsp_seat_number", e.target.value)} className="form-input text-sm" data-testid="add-deal-seat" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="form-input w-full text-sm" data-testid="add-deal-status">
                {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Media types">
              <MultiSelectTag label="media" options={MEDIA_TYPES} selected={form.media_type} onChange={(v) => set("media_type", v)} placeholder="Select media types" />
            </Field>
            <Field label="Devices">
              <MultiSelectTag label="device" options={DEVICES} selected={form.device} onChange={(v) => set("device", v)} placeholder="Select devices" />
            </Field>
            <Field label="Environments">
              <MultiSelectTag label="environment" options={ENVIRONMENTS} selected={form.environment} onChange={(v) => set("environment", v)} placeholder="Select environments" />
            </Field>
            <Field label="Geographies">
              <MultiSelectTag label="geography" options={COUNTRIES} selected={form.geography} onChange={(v) => set("geography", v)} placeholder="Select countries" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Primary KPI">
              <select value={form.primary_kpi} onChange={(e) => set("primary_kpi", e.target.value)} className="form-input w-full text-sm" data-testid="add-deal-kpi">
                <option value="">None</option>
                {KPI_OPTIONS.map((k) => (<option key={k} value={k}>{k}</option>))}
              </select>
            </Field>
            <Field label="KPI value">
              <Input value={form.primary_kpi_value} onChange={(e) => set("primary_kpi_value", e.target.value)} className="form-input text-sm" data-testid="add-deal-kpi-value" />
            </Field>
            <Field label="Currency">
              <Input value={form.primary_kpi_currency} onChange={(e) => set("primary_kpi_currency", e.target.value)} className="form-input text-sm" data-testid="add-deal-kpi-currency" />
            </Field>
          </div>

          <Field label="Custom request">
            <textarea value={form.custom_request} onChange={(e) => set("custom_request", e.target.value)} rows={3} className="form-input w-full text-sm" data-testid="add-deal-custom" />
          </Field>

          <button onClick={handleSubmit} disabled={saving} className="ingenium-button w-full" data-testid="add-deal-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create Deal"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddDealModal;
